import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { UIPaths } from '@consts';
import { useGoalRecommendationsService } from '@hooks/useGoalRecommendationsService';
import RocketIcon from '@icons/RocketIcon';
import XIcon from '@icons/XIcon';
import * as Sentry from '@sentry/nextjs';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { ReactElement, useEffect, useState } from 'react';

export default function GoalRecommendationBanner(): ReactElement | null {
  const { t } = useTranslation();
  const router = useRouter();
  const { getGoalsRecommendationsCount, markRecommendationsSeen } = useGoalRecommendationsService();

  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    getGoalsRecommendationsCount('banner')
      .then((res) => setCount(res.count))
      .catch((err) => {
        Sentry.captureException(err);
        setCount(0);
      });
  }, []);

  const handleClose = () => {
    markRecommendationsSeen('banner');
    setCount(0);
  };

  const handleCtaClick = () => {
    markRecommendationsSeen('banner');
    router.push(UIPaths.explore());
    setCount(0);
  };

  if (count <= 0) return null;

  return (
    <div
      className={classNames(
        'flex flex-row items-center justify-center gap-sm relative',
        setupForDark('bg-green-500'),
        // RESPONSIVE STYLES
        'sm:p-sm p-xs'
      )}
    >
      <div
        className={classNames(
          'flex min-w-0',
          'justify-center items-center uppercase P4R sm:P2R',
          setupForDark('text-white'),
          // RESPONSIVE STYLES
          'sm:flex-row flex-col',
          'sm:gap-sm gap-xs'
        )}
      >
        <div
          className={classNames(
            'flex flex-row items-center gap-sm',
            // RESPONSIVE STYLES
            'sm:flex hidden'
          )}
        >
          <div>{t('components.goalRecommendationBanner.title')}</div>
        </div>
        <div
          onClick={handleCtaClick}
          className={classNames(
            'border-[1px] px-sm py-xxs rounded-xs flex flex-row items-center gap-xs duration-100 cursor-pointer',
            setupForDark('text-white hover:scale-105')
          )}
        >
          <RocketIcon size={20} />
          {t('components.goalRecommendationBanner.cta')}
        </div>
      </div>
      <XIcon
        size={20}
        className={classNames(
          'cursor-pointer duration-100 absolute right-[16px]',
          setupForDark('text-white hover:scale-110')
        )}
        onClick={handleClose}
      />
    </div>
  );
}
